import { useEffect, useMemo, useState } from 'react';
import { getOfficeHours } from '@frontend/api-office-hours';
import { Schedule } from '@frontend/api-schedule-availability';
import {
  type ServiceProvider,
  deleteUploadedPhoto,
  getProviderName,
  upsertServiceProvider,
  updateServiceProviderHours,
} from '@frontend/api-service-providers';
import { useTranslation } from '@frontend/i18n';
import {
  FormRow,
  Modal,
  ModalControlModalProps,
  useForm,
  TextField,
  DropdownField,
  useFormField,
  useAlert,
  SearchField,
} from '@frontend/design-system';
import { AvatarEditor } from '../Avatar';
import { getInitials } from '../ServiceProviderAvatar';
import { ResponsiveModalActions } from '../settings';

interface Props extends ModalControlModalProps {
  provider: ServiceProvider;
  unifyProviders?: ServiceProvider[];
  isMultiLocation: boolean;
  isChildLocation: boolean;
  refetch: () => void;
  refetchUnifyProviders: () => void;
  locationId: string;
  schedule?: Schedule;
  writebacksFeatureFlag?: boolean;
}

export function ServiceProviderModal({
  provider,
  unifyProviders,
  isMultiLocation,
  isChildLocation,
  refetch,
  refetchUnifyProviders,
  locationId,
  schedule,
  writebacksFeatureFlag = false,
  ...modalControlProps
}: Props) {
  const { t } = useTranslation('schedule');
  const alert = useAlert();
  const [file, setFile] = useState<File>();

  const shouldMapWeaveProviderWithPMSProvider = writebacksFeatureFlag && isMultiLocation && isChildLocation;

  const providerSearchField = useFormField({ type: 'text', value: '' });

  const unifyProviderList = useFormField(
    {
      type: 'dropdown',
      value: provider?.parentProviderId ?? '',
      required: shouldMapWeaveProviderWithPMSProvider,
      validateOnChange: true,
      touched: true,
      validator: (value) => {
        if (shouldMapWeaveProviderWithPMSProvider && !value) {
          return t('Please select a provider');
        }
        return '';
      },
    },
    [shouldMapWeaveProviderWithPMSProvider, provider?.parentProviderId]
  );

  const { formProps, getFieldProps, reset, seedValues, isComplete } = useForm({
    fields: {
      firstName: { type: 'text', required: true },
      lastName: { type: 'text', required: true },
      publicDisplayName: { type: 'text', required: false },
    },
    onSubmit: async (values) => {
      let toSave = { ...provider, ...values, isActive: true };
      if (unifyProviderList.value) {
        toSave = { ...provider, ...values, parentProviderId: unifyProviderList.value ?? '', isActive: true };
      }
      delete toSave.createdAt;
      delete toSave.updatedAt;
      try {
        const serviceProviderData = await upsertServiceProvider(toSave, locationId, file);
        if (toSave.id) {
          alert.success(t('Successfully updated provider'));
        } else {
          alert.success(t('Successfully created provider'));
          if (serviceProviderData && serviceProviderData.id) {
            const officeHours = await getOfficeHours(locationId);
            if (officeHours) {
              await updateServiceProviderHours(serviceProviderData.id, locationId, officeHours);
            }
          }
        }
      } catch (error: any) {
        if (error && toSave.id) {
          alert.error(t('Failed to update provider'));
        } else {
          alert.error(t('Failed to create provider'));
        }
      } finally {
        modalControlProps.onClose();
        refetch();
        refetchUnifyProviders();
        setFile(undefined);
        reset();
      }
    },
  });

  useEffect(() => {
    if (unifyProviderList?.value && unifyProviders?.length) {
      const unifyProvider = unifyProviders?.find(({ id }) => id === unifyProviderList?.value);
      seedValues({
        firstName: unifyProvider?.firstName,
        lastName: unifyProvider?.lastName,
      });
    }
  }, [unifyProviderList?.value, unifyProviders]);

  const mode = useMemo(() => {
    return provider?.id ? t('Update') : t('Create');
  }, [provider]);

  useEffect(() => {
    reset();
    if (provider) {
      seedValues({ ...provider });
    }
  }, [provider]);

  const handleSaveProvider = (event?: React.FormEvent<Element>) => {
    if (!event) {
      return;
    }
    formProps.onSubmit(event);
  };

  const displayName = useMemo(() => {
    if (provider?.publicDisplayName || (provider?.firstName && provider?.lastName)) {
      const { publicDisplayName, firstName, lastName } = provider;
      return `${firstName} ${lastName}` || publicDisplayName;
    }
    return t('Provider');
  }, [provider]);

  const sortedUnifyProviders = useMemo(
    () =>
      unifyProviders
        ?.slice()
        .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)),
    [unifyProviders]
  );

  const unifyProviderOptions = useMemo(() => {
    const searchText = providerSearchField.value?.toLowerCase() ?? '';
    const filtered = searchText
      ? sortedUnifyProviders?.filter(({ firstName, lastName }) =>
          `${firstName} ${lastName}`.toLowerCase().includes(searchText)
        )
      : sortedUnifyProviders;
    return filtered?.map((p) => (
      <DropdownField.Option key={p.id} value={p.id}>
        {`${p.firstName} ${p.lastName} `}
      </DropdownField.Option>
    ));
  }, [sortedUnifyProviders, providerSearchField.value]);

  const handleCancel = () => {
    reset();
    if (provider) {
      seedValues({ ...provider });
    }
    modalControlProps.onClose();
  };

  const shouldDisableSave = !isComplete || (shouldMapWeaveProviderWithPMSProvider && !unifyProviderList.value);

  return (
    <Modal.Responsive {...modalControlProps} maxWidth={600}>
      <Modal.Header>
        {mode} {displayName}
      </Modal.Header>
      <Modal.Body>
        <AvatarEditor
          initials={provider && getInitials(provider)}
          uploadImageLabel={t('Upload Image')}
          removeImageLabel={t('Remove Photo')}
          alt={provider && getProviderName(provider, t('default provider name'))}
          src={provider?.publicDisplayImage}
          onDelete={async () => {
            await deleteUploadedPhoto(provider?.id);
            setFile(undefined);
            refetch();
          }}
          onChange={(file: File) => {
            setFile(file);
          }}
        />
        <form>
          {isMultiLocation && !!unifyProviders?.length && (
            <FormRow>
              <DropdownField name='provider' label={t('Parent Office Provider List')} {...unifyProviderList}>
                <SearchField
                  {...providerSearchField}
                  name='provider-search'
                  css={{ margin: 'var(--fabric-space-gap-xs)' }}
                />
                {unifyProviderOptions}
              </DropdownField>
            </FormRow>
          )}
          <FormRow>
            <TextField {...getFieldProps('firstName')} label={t('First Name')} />
            <TextField {...getFieldProps('lastName')} label={t('Last Name')} />
          </FormRow>
          <FormRow>
            <TextField {...getFieldProps('publicDisplayName')} label={t('Display Name')} />
          </FormRow>
        </form>
      </Modal.Body>
      <ResponsiveModalActions
        onPrimaryClick={handleSaveProvider}
        primaryLabel={t('Save')}
        onSecondaryClick={handleCancel}
        secondaryLabel={t('Cancel')}
        disablePrimary={shouldDisableSave}
      />
    </Modal.Responsive>
  );
}
